import { Alert, Flex, Form, FormInstance, Select, TableColumnsType, Tag } from 'antd';
import * as Component from 'components';
import {
  TransferDataHandlerInterface,
  TransferDataSourceInterface,
} from 'components/transfer/Transfer';
import { ContentInterface } from 'content/type';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { contentApiService } from 'service/api/contentApiService';
import { playlistRuleService } from '../services/playlistRuleService';

/**
 * Content types worth ordering shorts from — a work order needs a full-length source video.
 * `trailer`/`clip`/`shorts` are already short, and `live_streaming` has no stored mp4.
 */
const SOURCE_CONTENT_TYPE_OPTIONS = [
  { value: 'tv-series,movie', label: 'Episodes + Movies' },
  { value: 'tv-series', label: 'Episodes only' },
  { value: 'movie', label: 'Movies only' },
];

const DEFAULT_CONTENT_TYPES = SOURCE_CONTENT_TYPE_OPTIONS[0].value;

/**
 * Picks individual contents (episodes **or movies**) for the playlist — the `content` rule.
 *
 * Series/season rules only reach episodes that hang off a season, so a standalone movie was
 * previously unreachable from this screen even though the backend has always accepted
 * `value_type: 'content'` (`PlaylistRuleService.extract_rule_params`).
 */
export const ContentRule: React.FC<{
  form: FormInstance;
  /** Adds the work-order caveats — the bebridge feed narrows this selection further. */
  isWorkOrder?: boolean;
}> = ({ form, isWorkOrder }) => {
  const { id } = useParams<{ id: string }>();
  const [contentTypes, setContentTypes] = useState<string>(DEFAULT_CONTENT_TYPES);

  const columns: TableColumnsType<TransferDataSourceInterface> = [
    {
      dataIndex: 'id',
      title: 'Id',
      width: '10%',
    },
    {
      dataIndex: 'name',
      title: 'Name',
      render: (_, record): React.ReactNode => (
        <Flex align="center" gap="small">
          <span>{record.name}</span>
        </Flex>
      ),
    },
    {
      dataIndex: 'content_type',
      title: 'Type',
      width: '20%',
      render: (value: string): React.ReactNode => (
        <Tag color={value === 'movie' ? 'purple' : 'blue'}>{value}</Tag>
      ),
    },
  ];

  const filterOption = (input: string, item: ContentInterface): boolean =>
    item.title?.includes(input);

  const mapToTransferItem = (content: ContentInterface): TransferDataSourceInterface => ({
    ...content,
    key: content.id,
    name: content.title,
    disabled: false,
    poster: undefined,
  });

  const dataHandler: TransferDataHandlerInterface = {
    fetchSourceData: async (query: Record<string, string>) => {
      const extendedQuery = new URLSearchParams(query);
      extendedQuery.set('content_type', contentTypes);
      const { results, count } = await contentApiService.fetch(extendedQuery);
      return {
        data: results.map(mapToTransferItem),
        count,
      };
    },
    fetchTargetData: async (query: Record<string, string>) => {
      // No content_type filter here — whatever is already selected must stay visible,
      // otherwise switching the filter above would hide items you cannot then remove.
      const { results, count } = await playlistRuleService.fetchContentRules(
        form,
        Number(id),
        new URLSearchParams(query)
      );
      return {
        data: results.map(mapToTransferItem),
        count,
      };
    },
    createWithParentId: async (ids: number[]) => {
      const contentIds = form.getFieldValue('content');
      if (!contentIds) {
        form.setFieldValue('content', ids);
      } else {
        form.setFieldValue('content', [...new Set([...contentIds, ...ids])]);
      }
    },
    deleteWithParentId: async (ids: number[]) => {
      const contentIds = form.getFieldValue('content');
      if (!contentIds) {
        form.setFieldValue('content', []);
      } else {
        form.setFieldValue(
          'content',
          contentIds.filter((contentId: number) => !ids.includes(contentId))
        );
      }
    },
  };

  return (
    <div>
      <h3 style={{ display: 'flex', alignItems: 'center' }}>
        <span>Content (Episode / Movie)</span>
        <span
          style={{
            flex: 1,
            borderBottom: '1px solid #666',
            marginLeft: '8px',
            height: '0.5em',
          }}
        />
      </h3>

      {isWorkOrder && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: '12px' }}
          message="Pick individual episodes or movies here — series and season rules only reach episodes."
          description={
            'The work order feed then keeps only items whose On Air Date falls inside its ' +
            'window. A movie with no On Air Date never reaches the vendor, so set that date ' +
            'on the movie first.'
          }
        />
      )}

      <Select
        value={contentTypes}
        onChange={setContentTypes}
        options={SOURCE_CONTENT_TYPE_OPTIONS}
        style={{ width: '220px', marginBottom: '12px' }}
      />

      <Form.Item name="content" noStyle>
        <Component.TransferComponent
          dataHandler={dataHandler}
          filterOption={filterOption}
          columns={columns}
        />
      </Form.Item>
    </div>
  );
};

export default ContentRule;
